import {
  FC, MutableRefObject, useCallback, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Box, Card, CardHeader, Switch, Typography, useTheme,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import { getIsOrdersLoading } from '../../../../store/orders/selectors';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { useTable } from '../../../../hooks/useTable';
import { AnimatedDivider } from '../../../../components';
import { TSingleTeam } from '../../../../types';

type TMemberStatsTable = TSingleTeam;

const MemberStatsTable: FC<TMemberStatsTable> = ({ team }) => {
  const { t } = useTranslation(['orders', 'dashboard', 'common', 'inputs', 'commissions', 'team']);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const { localText, handleScrollTable } = useTable(apiRef);
  const theme = useTheme();
  const [fullHeight, setFullHeight] = useState(false);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const handleHeightToggle = useCallback(() => setFullHeight((prev) => !prev), []);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'netSales',
      headerName: t('netSales', { ns: 'commissions' }),
      type: 'number',
      flex: 1,
    },
    {
      field: 'commission',
      headerName: t('commission', { ns: 'commissions' }),
      type: 'number',
      flex: 1,
    },
    {
      field: 'salesPercent',
      headerName: t('ofTeamSales'),
      type: 'number',
      flex: 1,
      valueGetter: (_, row) => (
        team.netSales > 0 ? (((row?.netSales || 0) / team.netSales) * 100).toFixed(2) : '0.00'
      ),
      valueFormatter: (value) => `${value}%`,
    },
    {
      field: 'commissionPercent',
      headerName: t('ofTeamCommission'),
      type: 'number',
      flex: 1,
      valueGetter: (_, row) => (
        team.commission > 0 ? (((row?.commission || 0) / team.commission) * 100).toFixed(2) : '0.00'
      ),
      valueFormatter: (value) => `${value}%`,
    },
  ];

  return (
    <Card sx={{ height: fullHeight ? 'auto' : 550, pb: 7.5 }}>
      <CardHeader
        sx={{ p: 2 }}
        title={t('memberPerformance')}
        action={(
          <Stack direction="row" alignItems="center">
            <Box display="flex" alignItems="center">
              <Typography variant="body2">{t('fullTable', { ns: 'orders' })}</Typography>
              <Switch checked={fullHeight} onChange={handleHeightToggle} />
            </Box>
            <Box ml={2} display="flex" alignItems="center">
              <Box onClick={() => handleScrollTable('left')} className="cursor-pointer">
                <Icon color={theme.palette.primary.main} path={mdiChevronLeft} size={1.25} />
              </Box>
              <Box onClick={() => handleScrollTable('right')} className="cursor-pointer">
                <Icon color={theme.palette.primary.main} path={mdiChevronRight} size={1.25} />
              </Box>
            </Box>
          </Stack>
        )}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <DataGridPro
        rows={team.members.map((m, index) => ({ id: index, ...m }))}
        columns={columns}
        apiRef={apiRef}
        localeText={localText}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        disableColumnMenu
        hideFooterPagination
        hideFooter
      />
    </Card>
  );
};

export {
  MemberStatsTable,
};
